import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import { getFilePartyFromMeta, InvoiceProps, PriceVat } from './InvoiceForm'
import { useFormikContext } from 'formik'
import { FilePartyMetaDataDto, InvoiceMutationDto } from '../../../../config/finance/invoiceConfig'
import { Button, Field, InputOnChangeData, Label, Link, Tooltip } from '@fluentui/react-components'
import { DeleteRegular } from '@fluentui/react-icons'
import { Stack, StackItem } from '@fluentui/react'
import LegacyFilePartyDropdown, { IFilePartyDropdownOption } from '../../../Dropdown/LegacyFilePartyDropdown'
import { getDropdownProps } from '../../../../helpers/formHelper'
import useCurrency from '../../../../hooks/useCurrency'
import MoneyText from '../../../Text/Currency/MoneyText'
import { reducePrices } from '../../../../helpers/priceHelper'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import FormikInputField from '../../../Formik/FormikInputField'
import FormattableNumberInput, { CommitEventType, FormattableNumberInputProps } from '../../../Input/FormattableNumberInput'
import FormikField from '../../../Formik/FormikField'
import { parseNumber } from '../../../../helpers/numberHelper'

export type ChildProps = FieldArrayRenderProps &
    InvoiceProps & {
        child: InvoiceMutationDto
        index: number
        prefix: string
        isDragging: boolean
        filePartyOptions?: IFilePartyDropdownOption<FilePartyMetaDataDto>[]
    }

function Actions(props: ChildProps): ReactElement {
    return (
        <Stack horizontal verticalAlign="center">
            <StackItem>
                <Tooltip content="Verwijder cliënt" relationship="description" withArrow>
                    <Button appearance="transparent" icon={<DeleteRegular />} onClick={() => props.remove(props.index)} />
                </Tooltip>
            </StackItem>
        </Stack>
    )
}

function Aside(props: ChildProps): ReactElement {
    const { values } = useFormikContext<InvoiceMutationDto>()
    const { currency } = useCurrency(values.currency)

    const totalFactor = useMemo<number>(() => values.children?.map(child => child.factor).reduce((a, b) => a + b, 0), [values.children])
    const price = useMemo<PriceVat>(
        () =>
            [...props.lineItemGroupPrices.values()].reduce(reducePrices, {
                subtotal: 0,
                vat: [],
                total: 0,
            }),
        [props.lineItemGroupPrices],
    )
    const factoredPrice = useMemo<PriceVat>(() => {
        return {
            subtotal: (price.subtotal / totalFactor) * props.child.factor,
            vat: [],
            total: (price.total / totalFactor) * props.child.factor,
        }
    }, [totalFactor, price])

    return (
        <StackItem>
            <MoneyText amount={factoredPrice.subtotal} currency={currency} textProps={{ size: 400 }} />
        </StackItem>
    )
}

export function InvoiceChildContent(props: ChildProps): ReactElement {
    const history = useHistory()
    const { t: translate } = useTranslation()

    const { values, setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const [clientReference, setClientReference] = useState<string>('')

    const fileParties = useMemo(() => props.meta.data.files?.flatMap(file => file.fileParties) || [], [props.meta.data.files])

    useEffect(() => {
        if (props.child.debtorPivotId) {
            const recipient = getFilePartyFromMeta(props.child.debtorPivotId, undefined, undefined, fileParties)?.[0]
            const recipients = getFilePartyFromMeta(undefined, recipient?.role, recipient?.id, fileParties)

            const referenceRequired = recipients
                .map(recipient => recipient.referenceRequired)
                .filter(Boolean)
                .reduce((a, b) => a || b, false)
            setFieldValue(`${props.prefix}.recipientReferenceRequired`, referenceRequired)

            setClientReference(
                recipients
                    .map(recipient => recipient.reference)
                    .filter(Boolean)
                    .join(', '),
            )

            setFieldValue(
                `${props.prefix}.recipientEmail`,
                recipients
                    .map(recipient => recipient.email)
                    .filter(Boolean)
                    .join(', '),
            )
        }
    }, [values.children?.[props.index]?.debtorPivotId])

    useEffect(() => {
        setFieldValue(`${props.prefix}.recipientReference`, clientReference)
        values.children[props.index].recipientReference = clientReference
    }, [clientReference])

    return (
        <TreeItem itemType={'leaf'} onClick={event => event.preventDefault()}>
            <TreeItemLayout actions={<Actions {...props} />} aside={<Aside {...props} />}>
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 'm' }} grow>
                    <Field label={<Label weight={'semibold'}>{translate('base.table.header.number')}</Label>} orientation={'vertical'}>
                        {props.child.id && <Link onClick={() => history.push(`/invoice/${props.child.id}/edit`)}>{props.child.number}</Link>}
                        {!props.child.id && <Label>{props.child.number}</Label>}
                    </Field>
                    <StackItem>
                        <LegacyFilePartyDropdown
                            label={translate('invoice.clientpivotid')}
                            required
                            disabled={props.meta.loading}
                            options={props.filePartyOptions}
                            {...getDropdownProps(values.children?.[props.index]?.clientPivotId, undefined, value =>
                                setFieldValue(`${props.prefix}.clientPivotId`, value),
                            )}
                        />
                    </StackItem>
                    <StackItem>
                        <LegacyFilePartyDropdown
                            label={translate('invoice.debtorpivotid')}
                            required
                            disabled={props.meta.loading}
                            options={props.filePartyOptions}
                            {...getDropdownProps(values.children?.[props.index]?.debtorPivotId, undefined, value =>
                                setFieldValue(`${props.prefix}.debtorPivotId`, value),
                            )}
                        />
                    </StackItem>
                    <FormikInputField<any>
                        required={props.child.recipientReferenceRequired}
                        labelKey={'invoice.edit.form.header.recipientreference.label'}
                        fieldName={`${props.prefix}.recipientReference`}
                        componentProps={{
                            onChange: (_, { value }: InputOnChangeData) => setClientReference(value?.trim()),
                        }}
                    />
                    <FormikField<FormattableNumberInputProps, any>
                        required
                        labelKey={'invoice.edit.form.header.debtorfactor'}
                        fieldName={`${props.prefix}.factor`}
                        component={FormattableNumberInput}
                        componentProps={{
                            allowNegative: false,
                            decimals: 2,
                            onCommit: (eventType: CommitEventType, value: string) => {
                                if (eventType !== CommitEventType.OnLoad) {
                                    setFieldValue(`${props.prefix}.factor`, parseNumber(value, 'en'))
                                }
                            },
                        }}
                    />
                </Stack>
            </TreeItemLayout>
        </TreeItem>
    )
}
